import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Calendar, Clock, AlertCircle, CheckCircle } from 'lucide-react';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Badge } from '@/components/Badge';
import { Loading } from '@/components/Loading';
import { Modal } from '@/components/Modal';
import { BusinessHoursCalendarForm } from '@/components/sla/BusinessHoursCalendarForm';
import { slaService } from '@/services/slaService';
import type { BusinessHoursCalendar as BusinessHoursCalendarData, CreateBusinessHoursCalendarRequest, BusinessHours } from '@/types/sla';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

/**
 * Business Hours Calendar Management Page
 * Manage business hours and holidays for SLA calculations
 */
export const BusinessHoursCalendar: React.FC = () => {
  useDocumentTitle('Business Hours Calendars | SupportDesk');
  const [calendars, setCalendars] = useState<BusinessHoursCalendarData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCalendar, setSelectedCalendar] = useState<BusinessHoursCalendarData | null>(null);

  useEffect(() => {
    loadCalendars();
  }, []);

  const loadCalendars = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await slaService.getBusinessHoursCalendars();
      setCalendars(data);
    } catch (err) {
      setError('Failed to load business hours calendars');
      console.error('Error loading calendars:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (data: CreateBusinessHoursCalendarRequest) => {
    try {
      await slaService.createBusinessHoursCalendar(data);
      setShowCreateModal(false);
      await loadCalendars();
    } catch (err) {
      console.error('Error creating calendar:', err);
      throw err;
    }
  };

  const handleEdit = async (data: CreateBusinessHoursCalendarRequest) => {
    if (!selectedCalendar) return;
    
    try {
      await slaService.updateBusinessHoursCalendar(selectedCalendar.id, data);
      setShowEditModal(false);
      setSelectedCalendar(null);
      await loadCalendars();
    } catch (err) {
      console.error('Error updating calendar:', err);
      throw err;
    }
  };

  const handleDelete = async () => {
    if (!selectedCalendar) return;
    
    try {
      await slaService.deleteBusinessHoursCalendar(selectedCalendar.id);
      setShowDeleteModal(false);
      setSelectedCalendar(null);
      await loadCalendars();
    } catch (err) {
      console.error('Error deleting calendar:', err);
      setError('Failed to delete calendar. It may be in use by SLA policies.');
    }
  };

  const formatTime = (timeString?: string): string => {
    if (!timeString) return 'Closed';
    const parts = timeString.split(':');
    const hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);
    

    
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  const getBusinessDays = (calendar: BusinessHoursCalendarData): string[] => {
    if (!calendar.businessHours || calendar.businessHours.length === 0) {
      return [];
    }

    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const businessDays = calendar.businessHours
      .filter(bh => bh.isBusinessDay)
      .map(bh => dayNames[bh.dayOfWeek])
      .filter(Boolean);

    return [...new Set(businessDays)].sort((a, b) => {
      const aIndex = dayNames.indexOf(a);
      const bIndex = dayNames.indexOf(b);
      return aIndex - bIndex;
    });
  };

  const getOperatingHours = (calendar: BusinessHoursCalendarData): string => {
    if (!calendar.businessHours || calendar.businessHours.length === 0) {
      return 'Not configured';
    }

    // A single summary time is only accurate when every open day matches.
    const patterns: { [key: string]: number } = {};
    
    calendar.businessHours
      .filter(bh => bh.isBusinessDay)
      .forEach(bh => {
        const pattern = `${formatTime(bh.startTime)} - ${formatTime(bh.endTime)}`;
        patterns[pattern] = (patterns[pattern] || 0) + 1;
      });

    const configuredPatterns = Object.keys(patterns);
    if (configuredPatterns.length > 1) return 'Varies by day; see weekly schedule';
    return configuredPatterns[0] || 'Not configured';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <Loading size="lg" />
      </div>
    );
  }

  if (error && calendars.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <Card className="p-6 text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading Calendars</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <Button onClick={loadCalendars} variant="primary">
            Try Again
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Business Hours Calendars</h1>
          <p className="text-gray-600 mt-1">
            Configure business hours and holidays for accurate SLA calculations
          </p>
        </div>
        <Button
          variant="primary"
          icon={<Plus className="h-4 w-4" />}
          onClick={() => setShowCreateModal(true)}
        >
          Create Calendar
        </Button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
            <span className="text-red-700">{error}</span>
          </div>
        </div>
      )}

      {/* Calendars */}
      {calendars.length === 0 ? (
        <Card className="p-12 text-center">
          <Calendar className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Business Hours Calendars</h3>
          <p className="text-gray-600 mb-6">
            Create your first business hours calendar to define when your support team is available.
          </p>
          <Button
            variant="primary"
            icon={<Plus className="h-4 w-4" />}
            onClick={() => setShowCreateModal(true)}
          >
            Create First Calendar
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {calendars.map((calendar) => (
            <Card key={calendar.id} className="p-6">
              {/* Calendar Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{calendar.name}</h3>
                  {calendar.description && (
                    <p className="text-sm text-gray-600 mb-2">{calendar.description}</p>
                  )}
                  <div className="flex items-center space-x-2">
                    <Badge variant={calendar.isActive ? 'success' : 'secondary'}>
                      {calendar.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                    <Badge variant="secondary">
                      {calendar.timeZone}
                    </Badge>
                  </div>
                </div>
                <div className="flex items-center space-x-1 ml-4">
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={`Edit ${calendar.name}`}
                    icon={<Edit className="h-4 w-4" />}
                    onClick={() => {
                      setSelectedCalendar(calendar);
                      setShowEditModal(true);
                    }}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={`Delete ${calendar.name}`}
                    icon={<Trash2 className="h-4 w-4" />}
                    onClick={() => {
                      setSelectedCalendar(calendar);
                      setShowDeleteModal(true);
                    }}
                  />
                </div>
              </div>

              {/* Operating Hours */}
              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-2">Operating Hours</h4>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="flex items-center space-x-2 mb-2">
                      <Clock className="h-4 w-4 text-gray-500" />
                      <span className="text-sm font-medium text-gray-700">
                        {getOperatingHours(calendar)}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Calendar className="h-4 w-4 text-gray-500" />
                      <span className="text-sm text-gray-600">
                        {getBusinessDays(calendar).join(', ') || 'No business days configured'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Weekly Schedule */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-2">Weekly Schedule</h4>
                  <div className="space-y-2">
                    {[
                      { dayOfWeek: 1, label: 'Monday' },
                      { dayOfWeek: 2, label: 'Tuesday' },
                      { dayOfWeek: 3, label: 'Wednesday' },
                      { dayOfWeek: 4, label: 'Thursday' },
                      { dayOfWeek: 5, label: 'Friday' },
                      { dayOfWeek: 6, label: 'Saturday' },
                      { dayOfWeek: 0, label: 'Sunday' }
                    ].map((day) => {
                      const businessHour = calendar.businessHours?.find(bh => bh.dayOfWeek === day.dayOfWeek);
                      const isOpen = businessHour?.isBusinessDay && businessHour?.startTime && businessHour?.endTime;
                      
                      return (
                        <div key={day.dayOfWeek} className="flex items-center justify-between text-sm">
                          <span className="text-gray-700 w-20">{day.label}</span>
                          <div className="flex items-center space-x-2">
                            {isOpen ? (
                              <CheckCircle className="h-4 w-4 text-green-500" />
                            ) : (
                              <div className="h-4 w-4 rounded-full bg-gray-300" />
                            )}
                            <span className={`${isOpen ? 'text-gray-900' : 'text-gray-500'}`}>
                              {isOpen ? `${formatTime(businessHour.startTime)} - ${formatTime(businessHour.endTime)}` : 'Closed'}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Holidays */}
                {calendar.holidays && calendar.holidays.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-2">
                      Holidays ({calendar.holidays.length})
                    </h4>
                    <div className="space-y-1">
                      {calendar.holidays.slice(0, 3).map((holiday) => (
                        <div key={holiday.id} className="flex items-center justify-between text-sm">
                          <span className="text-gray-700">{holiday.name}</span>
                          <span className="text-gray-500">
                            {new Date(holiday.date).toLocaleDateString()}
                          </span>
                        </div>
                      ))}
                      {calendar.holidays.length > 3 && (
                        <div className="text-sm text-gray-500">
                          +{calendar.holidays.length - 3} more holidays
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="mt-4 pt-4 border-t border-gray-200 text-xs text-gray-500">
                <div className="flex items-center justify-between">
                  <span>
                    Created {new Date(calendar.createdAt).toLocaleDateString()}
                  </span>
                  {calendar.updatedAt && (
                    <span>
                      Updated {new Date(calendar.updatedAt).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Create Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create Business Hours Calendar"
        size="lg"
        className="max-h-[90vh] overflow-y-auto"
      >
        <BusinessHoursCalendarForm
          onSubmit={handleCreate}
          onCancel={() => setShowCreateModal(false)}
        />
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedCalendar(null);
        }}
        title="Edit Business Hours Calendar"
        size="lg"
        className="max-h-[90vh] overflow-y-auto"
      >
        {selectedCalendar && (
          <BusinessHoursCalendarForm
            initialData={selectedCalendar}
            onSubmit={handleEdit}
            onCancel={() => {
              setShowEditModal(false);
              setSelectedCalendar(null);
            }}
          />
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedCalendar(null);
        }}
        title="Delete Business Hours Calendar"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Are you sure you want to delete the calendar "{selectedCalendar?.name}"? 
            This action cannot be undone and may affect SLA calculations.
          </p>
          <div className="flex items-center space-x-3 pt-4">
            <Button
              variant="danger"
              onClick={handleDelete}
              loading={loading}
            >
              Delete Calendar
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteModal(false);
                setSelectedCalendar(null);
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
